import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import api from "utils/api";

import {
  Alert,
  Box,
  Button,
  Grid,
  TextField,
  Stack,
  Typography,
  IconButton,
  Paper
} from "@mui/material";
import useFormValidation from "hooks/useFormValidation";
import { validators } from "utils/formValidation";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

export default function EditCategory() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const store_id = params.get("store_id");
  const store_name = params.get("store_name");

  const [loading, setLoading] = useState(false);
  const { formError, validate, setServerError, clearFieldError, fieldErrorProps } = useFormValidation();

  const [form, setForm] = useState({
    name_en: "",
    name_ar: "",
    sequence: 0,
  });

  const [image, setImage] = useState(null);
{ if (image && typeof image === "string") {
    setImage(null);
    } }
  const [times, setTimes] = useState([
    { from_time: "", to_time: "" }
  ]);

  useEffect(() => {
    fetchCategory();
  }, []);

    const fetchCategory = async () => {
        try {
            const res = await api.get(`/product/category/${id}`);
            const c = res.data.data;
        
            setForm({
                name_en: c.name_en || "",
                name_ar: c.name_ar || "",
                sequence: c.sequence || 0,
            }); 

            if (c.image_url) {
                setImage(c.image_url);
            }

            if (c.times && c.times.length > 0) {
                setTimes(c.times);
            }   
        } catch (err) {
            setServerError("Failed to load category data");
        }   
    };

  const handleChange = (key, value) => {
    clearFieldError(key);
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const addTime = () => {
    setTimes([...times, { from_time: "", to_time: "" }]);
  };

  const removeTime = (index) => {
    setTimes(times.filter((_, i) => i !== index));
  };

  const updateTime = (index, key, value) => {
    const updated = [...times];
    updated[index][key] = value;
    setTimes(updated);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validate({ name_en: [validators.required("Name (English)")] }, form)) return;

    setLoading(true);

    try {
      const formData = new FormData();

      formData.append("name_en", form.name_en);
      formData.append("name_ar", form.name_ar);
      formData.append("sequence", form.sequence);
      formData.append("store_id", store_id);

      formData.append("times", JSON.stringify(times));

      if (image) {
        formData.append("image", image);
      }

      await api.put(`/product/category/edit/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      navigate(-1);

    } catch (err) {
      setServerError(err.response?.data?.message || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>

      {/* HEADER */}
      <Stack direction="row" alignItems="center" gap={1} mb={2}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>

        <Typography variant="h5">
          Edit Category ({store_name})
        </Typography>
      </Stack>

      {formError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {formError}
        </Alert>
      ) : null}

      <Paper sx={{ p: 3 }} component="form" onSubmit={handleSubmit} noValidate>
        <Grid container spacing={2}>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Name (English)"
              value={form.name_en}
              onChange={(e) => handleChange("name_en", e.target.value)}
              {...fieldErrorProps("name_en")}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Name (Arabic)"
              value={form.name_ar}
              onChange={(e) => handleChange("name_ar", e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Sequence"
              type="number"
              value={form.sequence}
              onChange={(e) => handleChange("sequence", e.target.value)}
            />
          </Grid>

          {/* IMAGE */}
          <Grid item xs={12}>
            <Button variant="outlined" component="label">
              Upload Image
              <input hidden type="file" onChange={(e) => setImage(e.target.files[0])} />
            </Button>
          </Grid>

          {/* TIMES */}
          <Grid item xs={12}>
            <Typography variant="h6">Time Slots</Typography>

            {times.map((t, i) => (
              <Stack key={i} direction="row" spacing={1} mt={1}>
                <TextField
                  type="time"
                  value={t.from_time}
                  onChange={(e) => updateTime(i, "from_time", e.target.value)}
                />
                <TextField
                  type="time"
                  value={t.to_time}
                  onChange={(e) => updateTime(i, "to_time", e.target.value)}
                />

                {i !== 0 && (
                  <IconButton onClick={() => removeTime(i)}>
                    <DeleteIcon />
                  </IconButton>
                )}
              </Stack>
            ))}

            <Button startIcon={<AddIcon />} onClick={addTime} sx={{ mt: 1 }}>
              Add Time
            </Button>
          </Grid>

        </Grid>

        {/* SUBMIT */}
        <Stack alignItems="flex-end" mt={3}>
          <Button variant="contained" type="submit" disabled={loading}>
            {loading ? "Updating..." : "Update Category"}
          </Button>
        </Stack>

      </Paper>
    </Box>
  );
}